import * as React from 'react';
import { useMemo } from 'react';

import { get as getUser } from 'js/lib/user';

import SkillPathwayEnrollmentDataContext from 'bundles/skills-common/shared/verified-skills/context/skill-pathway/SkillPathwayEnrollmentDataContext';
import useGetEnterpriseProgramForCourse from 'bundles/skills-common/shared/verified-skills/data/pathway-badge/useGetEnterpriseProgramForCourse';
import useGetVerifiedSkillsEnterpriseBadgeCollection from 'bundles/skills-common/shared/verified-skills/data/pathway-badge/useGetVerifiedSkillsEnterpriseBadgeCollection';
import useGetProgramCurriculumQuery from 'bundles/skills-common/shared/verified-skills/data/skill-pathway/useGetProgramCurriculumQuery';
import useGetSkillPathwayCurriculumProductsQuery from 'bundles/skills-common/shared/verified-skills/data/skill-pathway/useGetSkillPathwayCurriculumProductsQuery';

type Props = {
  children: React.ReactNode;
  courseId: string;
  /** When true, enterprise program lookup is skipped (e.g. practice / ungraded flows). */
  skip?: boolean;
  overrideProgramId?: string;
  overrideCurriculumId?: string;
};

const SkillPathwayEnrollmentDataProvider = ({
  children,
  courseId,
  skip,
  overrideProgramId,
  overrideCurriculumId,
}: Props) => {
  const userId = getUser().id;

  const { program, loading: loadingEnterpriseProgram } = useGetEnterpriseProgramForCourse({
    userId,
    courseId,
    skip,
  });
  // when using outside the assessments context, like in home page, we need to use the override program values
  const programId = program?.id || overrideProgramId || '';
  const curriculumId = program?.metadata?.curriculumId || overrideCurriculumId || '';

  const {
    enrolledBadgeCollection,
    loading: loadingBadgeCollection,
    refetchEnrolledBadgeCollection,
  } = useGetVerifiedSkillsEnterpriseBadgeCollection({
    userId,
    courseId,
    programId,
  });

  const { curriculum, loading: loadingProgramCurriculum } = useGetProgramCurriculumQuery({
    curriculumId,
  });

  const skillPath = useMemo(() => {
    const badgeTemplateId = enrolledBadgeCollection?.badgeTemplateId || '';
    const matchingBadge = (_: { badgeTemplateId?: string }) => _.badgeTemplateId === badgeTemplateId;

    return (
      curriculum?.definition?.coreTracks?.find(matchingBadge) ??
      curriculum?.definition?.electiveTracks?.find(matchingBadge)
    );
  }, [
    enrolledBadgeCollection?.badgeTemplateId,
    curriculum?.definition?.coreTracks,
    curriculum?.definition?.electiveTracks,
  ]);

  const curriculumQueryIds = useMemo(() => {
    // when using outside the assessments context, like in home page, we need to use the overrideProgramId
    const programIdForQuery = program?.id || overrideProgramId || '';
    if (!programIdForQuery || !enrolledBadgeCollection?.productIds) {
      return [];
    }

    return enrolledBadgeCollection?.productIds?.map(
      (productId) => `${programIdForQuery}~VerifiedCertificate~${productId.id}`
    );
  }, [program?.id, overrideProgramId, enrolledBadgeCollection?.productIds]);

  const { skillPathCurriculumProducts, loading: loadingSkillPathCurriculumProducts } =
    useGetSkillPathwayCurriculumProductsQuery({
      curriculumQueryIds,
    });

  return (
    <SkillPathwayEnrollmentDataContext.Provider
      value={{
        program,
        skillPath,
        skillPathCurriculumProducts,
        enrolledBadgeCollection,
        refetchEnrolledBadgeCollection,
        loading:
          loadingEnterpriseProgram ||
          loadingBadgeCollection ||
          loadingProgramCurriculum ||
          loadingSkillPathCurriculumProducts,
      }}
    >
      {children}
    </SkillPathwayEnrollmentDataContext.Provider>
  );
};

export default SkillPathwayEnrollmentDataProvider;
